import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import Decimal from "decimal.js";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { API_BASE, api } from "../../api/client";
import type { Basis } from "./ReportsPage";
import { formatMoney } from "../../lib/money";

// 財務報表:單張報價單的財務總覽(業主 2026-09-30:「財務報表 新增對某張報價單的財務總攬」)。
//
// 業主選的內容:**成本與毛利**、**匯出 Excel**;選得到**全部**報價單(含已結案與封存的)。
// 營收、成本、毛利由後端算,與報價單「專案成本」那一塊是同一套(`services/profit.py`)——
// 這一頁一個加總都不做,理由與財務報表其餘部分相同:畫面與 Excel 不可以各有一份算法。

interface Pick {
  id: number;
  quote_no: string;
  title: string;
  archived: boolean;
}

interface CategoryLine {
  category: string;
  label: string;
  count: number;
  amount: string;
  share_percent: string | null;
}

interface ExpenseLine {
  id: number;
  spent_on: string;
  category_label: string;
  vendor: string;
  note: string;
  amount: string;
  recorder_name: string;
}

interface Finance {
  quote_id: number;
  quote_no: string;
  title: string;
  archived: boolean;
  revenue: string;
  cost: string;
  profit: string;
  margin_percent: string | null;
  cost_recorded: boolean;
  invoiced: boolean;
  /** 營收口徑與這張單成交了沒(2026-09-30)。 */
  basis?: Basis;
  deal?: boolean;
  by_category: CategoryLine[];
  expenses: ExpenseLine[];
}

function money(value: string | number | null | undefined): string {
  // 全系統金額一律 0,000,000、四捨五入到整數(業主 2026-10-15)
  return formatMoney(value);
}

function pickLabel(one: Pick): string {
  return `${one.quote_no} ${one.title}`.trim() + (one.archived ? "(已封存)" : "");
}

function Figure({ label, value, tone }: { label: string; value: string; tone?: "danger" }) {
  return (
    <div className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] px-4 py-3">
      <div className="text-xs font-medium text-[var(--text-2)]">{label}</div>
      <div
        className={
          "mt-1 text-xl font-semibold tabular-nums " +
          (tone === "danger" ? "text-[var(--danger)]" : "text-[var(--text)]")
        }
      >
        {value}
      </div>
    </div>
  );
}

export function QuoteFinance({ basis = "deal" }: { basis?: Basis } = {}) {
  const [needle, setNeedle] = useState("");
  const [picked, setPicked] = useState("");

  const picks = useQuery({
    queryKey: ["report-quote-picks"],
    queryFn: () => api.get<Pick[]>("/reports/quote-picks"),
  });
  const finance = useQuery({
    queryKey: ["report-quote", picked, basis],
    queryFn: () => api.get<Finance>(`/reports/quote/${picked}`, { basis }),
    enabled: picked !== "",
  });

  // 打單號或專案名稱縮小選單 —— 報價單一多,原生下拉只認得開頭那個字。
  const options = useMemo(() => {
    const all = Array.isArray(picks.data) ? picks.data : [];
    const key = needle.trim().toLowerCase();
    const shown = key ? all.filter((one) => pickLabel(one).toLowerCase().includes(key)) : all;
    return [
      { value: "", label: "請選一張報價單" },
      ...shown.map((one) => ({ value: String(one.id), label: pickLabel(one) })),
    ];
  }, [picks.data, needle]);

  const f = finance.data;
  const losing = f ? new Decimal(f.profit).isNegative() : false;

  return (
    <section aria-label="單張報價單財務總覽" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <Input
          label="找報價單"
          className="w-48"
          placeholder="單號或專案名稱"
          value={needle}
          onChange={(e) => setNeedle(e.target.value)}
        />
        <Select
          label="報價單"
          className="w-80"
          value={picked}
          onChange={(e) => setPicked(e.target.value)}
          options={options}
        />
        {picked ? (
          <a
            href={`${API_BASE}/reports/quote/${picked}.xlsx?basis=${basis}`}
            className="inline-flex h-9 items-center gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-3.5 text-sm font-medium text-[var(--text)] hover:bg-[var(--surface-2)]"
          >
            <Download aria-hidden="true" size={16} />
            匯出 Excel
          </a>
        ) : null}
      </div>

      {!picked ? (
        <p className="m-0 text-sm text-[var(--text-3)]">選一張報價單,看它的營收、成本與毛利。</p>
      ) : !f ? (
        <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Figure label={basis === "deal" ? "營收(成交金額)" : "營收(已開發票)"} value={money(f.revenue)} />
            <Figure label="成本(雜支合計)" value={money(f.cost)} />
            <Figure label="毛利" value={money(f.profit)} tone={losing ? "danger" : undefined} />
            <Figure label="毛利率" value={f.margin_percent === null ? "—" : `${f.margin_percent}%`} />
          </div>
          {(basis === "deal" ? !f.deal : !f.invoiced) || !f.cost_recorded ? (
            <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm text-[var(--warn)]">
              {basis === "deal" && !f.deal ? <li>還沒成交,營收以 0 計。</li> : null}
              {basis === "invoice" && !f.invoiced ? <li>還沒開發票,營收以 0 計。</li> : null}
              {!f.cost_recorded ? <li>還沒登錄雜支,毛利可能偏高。</li> : null}
            </ul>
          ) : null}

          <div className="overflow-x-auto">
            <table aria-label="成本依分類" className="w-full max-w-xl border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs text-[var(--text-3)]">
                  <th className="px-3 py-2 font-medium">分類</th>
                  <th className="px-3 py-2 text-right font-medium">筆數</th>
                  <th className="px-3 py-2 text-right font-medium">金額</th>
                  <th className="px-3 py-2 text-right font-medium">佔成本</th>
                </tr>
              </thead>
              <tbody>
                {f.by_category.map((c) => (
                  <tr key={c.category} className="border-t border-[var(--border)]">
                    <td className="px-3 py-2">{c.label}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{c.count}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(c.amount)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {c.share_percent === null ? "—" : `${c.share_percent}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="overflow-x-auto">
            <table aria-label="雜支明細" className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs text-[var(--text-3)]">
                  <th className="px-3 py-2 font-medium">日期</th>
                  <th className="px-3 py-2 font-medium">分類</th>
                  <th className="px-3 py-2 font-medium">店家</th>
                  <th className="px-3 py-2 font-medium">說明</th>
                  <th className="px-3 py-2 text-right font-medium">金額</th>
                  <th className="px-3 py-2 font-medium">登錄者</th>
                </tr>
              </thead>
              <tbody>
                {f.expenses.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-3 text-[var(--text-3)]">
                      這張單還沒有雜支。
                    </td>
                  </tr>
                ) : (
                  f.expenses.map((e) => (
                    <tr key={e.id} className="border-t border-[var(--border)]">
                      <td className="px-3 py-2 tabular-nums">{e.spent_on}</td>
                      <td className="px-3 py-2">{e.category_label}</td>
                      <td className="px-3 py-2">{e.vendor}</td>
                      <td className="px-3 py-2">{e.note}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(e.amount)}</td>
                      <td className="px-3 py-2">{e.recorder_name}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
